import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const DISMISS_KEY = "mb.broadcast.dismissed";
const POLL_MS = 60_000;

/**
 * Global banner shown at the top of every broker page. Dismissible
 * per user per banner-id (localStorage). Also functions as the
 * "You are impersonating…" banner when the current session is imp.
 */
export function GlobalBanner() {
  const { user, isImpersonating, exitImpersonation } = useAuth();
  const [broadcast, setBroadcast] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(DISMISS_KEY) || "[]")); }
    catch { return new Set(); }
  });

  useEffect(() => {
    let cancelled = false;
    const fetchIt = async () => {
      try {
        const { data } = await api.get("/broadcast/current");
        if (!cancelled) setBroadcast(data);
      } catch { /* silent */ }
    };
    fetchIt();
    const t = setInterval(fetchIt, POLL_MS);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const dismiss = (id) => {
    const next = new Set(dismissed);
    next.add(id);
    setDismissed(next);
    try { localStorage.setItem(DISMISS_KEY, JSON.stringify([...next])); } catch { /* silent */ }
  };

  // Impersonation banner overrides broadcast — a persistent, non-
  // dismissible amber bar so the admin never forgets they're inside
  // someone else's session.
  if (isImpersonating && user) {
    return (
      <div
        className="sticky top-0 z-40 flex items-center justify-between gap-3 bg-amber-500 px-4 py-2 text-xs font-semibold text-white shadow-md sm:text-sm"
        data-testid="impersonation-banner"
      >
        <span className="min-w-0 truncate">
          You are viewing MatchBroker as{" "}
          <span className="font-bold">{user.full_name || user.email}</span> —
          actions are logged.
        </span>
        <button
          onClick={exitImpersonation}
          className="mb-focus-ring inline-flex items-center gap-1 rounded-full bg-white/20 px-3 py-1 text-[11px] font-bold uppercase tracking-wider hover:bg-white/30"
          data-testid="impersonation-exit"
        >
          Exit impersonation
        </button>
      </div>
    );
  }

  if (!broadcast) return null;
  if (dismissed.has(broadcast.id)) return null;

  const tone = {
    info: "bg-brand-navy text-white",
    warning: "bg-amber-500 text-white",
    success: "bg-brand-green text-white",
  }[broadcast.style] || "bg-brand-navy text-white";

  return (
    <div
      className={`sticky top-0 z-40 flex items-center justify-between gap-3 px-4 py-2 text-xs font-medium shadow-sm sm:text-sm ${tone}`}
      data-testid="broadcast-banner"
    >
      <span className="min-w-0 truncate">{broadcast.message}</span>
      <button
        onClick={() => dismiss(broadcast.id)}
        aria-label="Dismiss"
        className="mb-focus-ring inline-flex h-6 w-6 items-center justify-center rounded-full hover:bg-white/20"
        data-testid="broadcast-dismiss"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
